/* eslint-disable no-unused-vars */

import { Trans } from '@lingui/macro';
import { forEach, get, groupBy, map, mapValues, noop } from 'lodash';
import { useTooltipStyles } from '../ChartTooltipStyles';
import { chartDefFormatter } from '../Formatters';
import { percentFormatter } from '../../../../utilities/FormattingUtilities';
import { VARIANT_OVERLAY } from '../../test-comparison-chart/TestComparisonChartHelper';

function empty(classes) {
    <tr>
        <td className={classes.noDataText} colSpan={3}>
            <Trans>No data available</Trans>
        </td>
    </tr>;
}

/**
 * Used for overlay chart, to show only the *primary* metric, both before & after
 */
function TestComparisonPrimaryMetric({ primaryMetric, simplifiedSeries }) {
    const classes = useTooltipStyles();
    if (simplifiedSeries.length === 0) {
        return empty(classes);
    }
    return (
        <>
            {simplifiedSeries.map((item) => {
                return (
                    <tr key={`${item.seriesName}`} className={classes.tooltipPoint}>
                        <td>
                            <div className={classes.tooltipPointColorBox} style={{ backgroundColor: item.color }} />
                        </td>
                        <td className={classes.tooltipPointLabel}>{item.seriesName}:</td>
                        <td className={classes.tooltipPointValue}>{chartDefFormatter(primaryMetric, item.value)}</td>
                    </tr>
                );
            })}
        </>
    );
}

/**
 * Used for breakdown chart, to show *all* metrics, before & after
 */
function TestComparisonMetricList({ simplifiedSeries }) {
    const classes = useTooltipStyles();
    if (simplifiedSeries.length === 0) {
        return empty(classes);
    }

    const pointsByMetricName = groupBy(simplifiedSeries, (seriesItem) => seriesItem?.metric?.name || 'invalid');

    const rows = mapValues(pointsByMetricName, (rowContents) => {
        const newRowAsArray = groupBy(rowContents, (point) => {
            return point.dataSource;
        });
        return mapValues(newRowAsArray, (arr) => {
            const foo = arr[0];
            return foo;
        });
    }); // rows is keyed by metric name.  Each row's points are keyed by dataSource

    return (
        <>
            <tr>
                <th />
                <th />
                <th className={classes.tooltipPointHeaderLabel}>
                    <div>
                        <Trans>Before</Trans>
                    </div>
                </th>
                <th className={classes.tooltipPointHeaderLabel}>
                    <div>
                        <Trans>After</Trans>
                    </div>
                </th>
                <th className={classes.tooltipPointHeaderLabel}>
                    <div>
                        <Trans>% Change</Trans>
                    </div>
                </th>
            </tr>

            {map(rows, (row) => (
                <tr key={row?.before?.metric?.name} className={classes.tooltipPoint}>
                    <td>
                        <div className={classes.tooltipPointColorBox} style={{ backgroundColor: row?.before?.color }} />
                    </td>
                    <td>
                        <div className={classes.tooltipPointLabel}> {row?.before?.seriesName}</div>
                    </td>
                    <td className={classes.tooltipPointValue}>
                        {chartDefFormatter(row?.before?.metric, row?.before?.value)}
                    </td>
                    <td className={classes.tooltipPointValue}>
                        {chartDefFormatter(row?.after?.metric, row?.after?.value)}
                    </td>
                    <td className={classes.tooltipPointValue}>{percentFormatter(row?.delta?.value)}</td>
                </tr>
            ))}
        </>
    );
}

export function TestComparisonTooltip({ point, variant }) {
    const classes = useTooltipStyles();
    if (!point) return null;

    // point is the Highcharts.Point being hovered over
    // point.series is a Highcharts.Series (the one point is in)
    // point.series.chart.series is ALL the series
    const { x } = point;
    const subSeries = get(point, 'series.chart.series', []);
    const simplifiedSeries = subSeries.map((aSeries) => {
        const dataAtThisX = aSeries?.options?.data[x]; // make sure to use options.data, because data may be un-set for hidden series like delta
        return {
            color: aSeries.color,
            dataSource: dataAtThisX?.dataSource,
            metric: dataAtThisX?.metric,
            seriesName: aSeries?.name,
            value: dataAtThisX?.value,
        };
    });

    return (
        <>
            <div className={classes.entityNameHeader}>{point.testMeta.testName}</div>

            <table className={classes.tooltipTable}>
                <tbody>
                    {variant === VARIANT_OVERLAY ? (
                        <TestComparisonPrimaryMetric
                            primaryMetric={point.testMeta?.primaryMetric}
                            simplifiedSeries={simplifiedSeries}
                        />
                    ) : (
                        <TestComparisonMetricList simplifiedSeries={simplifiedSeries} />
                    )}
                </tbody>
            </table>
        </>
    );
}
